import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Home, Building2, Warehouse, PaintBucket, Brush, Wrench } from 'lucide-react';

const services = [
  {
    icon: Home,
    title: 'Residential Roof Painting',
    description: 'Complete roof restoration for homes of all sizes. From single-story cottages to large family homes.',
    features: ['Tile Roofs', 'Colorbond', 'Heritage Homes']
  },
  {
    icon: Building2,
    title: 'Commercial Roofing',
    description: 'Professional roof painting services for commercial and industrial properties throughout the Blue Mountains.',
    features: ['Factories', 'Warehouses', 'Office Buildings']
  },
  {
    icon: Warehouse,
    title: 'Strata & Body Corporate',
    description: 'Specialized services for strata complexes and apartment buildings with minimal disruption.',
    features: ['Unit Blocks', 'Townhouses', 'Retirement Villages']
  },
  {
    icon: PaintBucket,
    title: 'Colorbond Roofing',
    description: 'Expert Colorbond roof painting with premium coatings designed for Australian conditions.',
    features: ['Re-coating', 'Color Change', 'Rust Treatment']
  },
  {
    icon: Brush,
    title: 'Terracotta & Concrete Tiles',
    description: 'Restore faded and weathered tiles to their original beauty with our specialized process.',
    features: ['Pressure Cleaning', 'Sealing', 'Re-pointing']
  },
  {
    icon: Wrench,
    title: 'Roof Repairs',
    description: 'Complete roof repair services including leak fixing, tile replacement, and gutter repairs.',
    features: ['Leak Detection', 'Tile Replacement', 'Guttering']
  }
];

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      id="services"
      className="py-20 md:py-32 bg-[#111A2E] relative"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(56, 189, 248, 0.8) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full glass text-[#F95738] text-sm font-medium mb-6">
            Our Services
          </span>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Professional Roof Solutions
          </h2>
          
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
            From residential homes to commercial properties, we deliver exceptional results every time
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              whileHover={{ y: -8 }}
              className="glass rounded-2xl p-6 group cursor-pointer border border-transparent hover:border-[#38BDF8]/30 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F95738]/20 to-[#38BDF8]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <service.icon className="w-7 h-7 text-[#38BDF8]" />
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#38BDF8] transition-colors">
                {service.title}
              </h3>
              
              <p className="text-[#94A3B8] text-sm mb-4 leading-relaxed">
                {service.description}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature) => (
                  <span 
                    key={feature}
                    className="px-3 py-1 rounded-full bg-[#0B111E]/50 text-xs text-[#94A3B8]"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-[#94A3B8] mb-6">
            Not sure what you need? We offer free inspections and honest advice.
          </p>
          <motion.a
            href="#quote"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 cta-button px-8 py-4 rounded-full text-white font-semibold"
          >
            Get a Free Quote
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
